import * as FirebaseAuth from "firebase/auth"
import { useEffect, useState } from "react"
import { auth } from "../../core"

const getErrorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message
  return String(e)
}

type AuthState =
  | "initial"
  | "loading"
  | { firebaseUser: FirebaseAuth.User }

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>("loading")

  useEffect(() => {
    const unsubscribe = FirebaseAuth.onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser !== null) {
        setAuthState({ firebaseUser })
      } else {
        setAuthState("initial")
      }
    })
    return unsubscribe
  }, [])

  const firebaseUser = typeof authState === "object" ? authState.firebaseUser : undefined

  const signInAnonymous = async () => {
    setAuthState("loading")
    try {
      await FirebaseAuth.signInAnonymously(auth)
    } catch (e) {
      console.error(e)
      window.alert(`匿名ログインに失敗しました。${getErrorMessage(e)}`)
      setAuthState("initial")
    }
  }

  const signInWithGoogle = async () => {
    setAuthState("loading")
    try {
      const provider = new FirebaseAuth.GoogleAuthProvider()
      provider.setCustomParameters({ prompt: "select_account" })
      await FirebaseAuth.signInWithPopup(auth, provider)
    } catch (e) {
      console.error(e)
      window.alert(`Googleログインに失敗しました。${getErrorMessage(e)}`)
      setAuthState("initial")
    }
  }

  const signInWithPhone = async (recaptchaContainerId: string) => {
    setAuthState("loading")
    try {
      const provider = new FirebaseAuth.PhoneAuthProvider(auth)
      const recaptchaVerifier = new FirebaseAuth.RecaptchaVerifier(auth, recaptchaContainerId, { size: "normal" })
      const phoneNumber = window.prompt("電話番号を入力してください（例: +819012345678）")
      if (!phoneNumber) {
        setAuthState("initial")
        return
      }
      const verificationId = await provider.verifyPhoneNumber(phoneNumber, recaptchaVerifier)
      const verificationCode = window.prompt("SMSで届いた確認コードを入力してください")
      if (!verificationCode) {
        setAuthState("initial")
        return
      }
      const credential = FirebaseAuth.PhoneAuthProvider.credential(verificationId, verificationCode)
      await FirebaseAuth.signInWithCredential(auth, credential)
    } catch (e) {
      console.error(e)
      window.alert(`電話番号ログインに失敗しました。${getErrorMessage(e)}`)
      setAuthState("initial")
    }
  }

  const linkWithGoogle = async (targetUser: FirebaseAuth.User) => {
    try {
      const provider = new FirebaseAuth.GoogleAuthProvider()
      provider.setCustomParameters({ prompt: "select_account" })
      const result = await FirebaseAuth.linkWithPopup(targetUser, provider)
      setAuthState({ firebaseUser: result.user })
    } catch (e) {
      console.error("Google アカウント連携エラー:", e)
      window.alert(`Google連携に失敗しました。${getErrorMessage(e)}`)
    }
  }

  const linkWithPhone = async (targetUser: FirebaseAuth.User, recaptchaContainerId: string) => {
    try {
      const provider = new FirebaseAuth.PhoneAuthProvider(auth)
      const recaptchaVerifier = new FirebaseAuth.RecaptchaVerifier(auth, recaptchaContainerId, { size: "normal" })
      const phoneNumber = window.prompt("連携する電話番号を入力してください（例: +819012345678）")
      if (!phoneNumber) return
      const verificationId = await provider.verifyPhoneNumber(phoneNumber, recaptchaVerifier)
      const verificationCode = window.prompt("SMSで届いた確認コードを入力してください")
      if (!verificationCode) return
      const credential = FirebaseAuth.PhoneAuthProvider.credential(verificationId, verificationCode)
      const result = await FirebaseAuth.linkWithCredential(targetUser, credential)
      setAuthState({ firebaseUser: result.user })
    } catch (e) {
      console.error("電話番号連携エラー:", e)
      window.alert(`電話番号連携に失敗しました。${getErrorMessage(e)}`)
    }
  }

  const unlinkProvider = async (targetUser: FirebaseAuth.User, providerId: string) => {
    try {
      const updatedUser = await FirebaseAuth.unlink(targetUser, providerId)
      setAuthState({ firebaseUser: updatedUser })
    } catch (e) {
      console.error("連携解除エラー:", e)
      window.alert(`連携解除に失敗しました。${getErrorMessage(e)}`)
    }
  }

  const signOut = async () => {
    await FirebaseAuth.signOut(auth)
    setAuthState("initial")
  }

  return {
    authState,
    firebaseUser,
    signInAnonymous,
    signInWithGoogle,
    signInWithPhone,
    linkWithGoogle,
    linkWithPhone,
    unlinkProvider,
    signOut,
  }
}
